/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {useHistory} from 'react-router'
import {
    Box,
    Heading,
    Text,
    Stack,
    Grid,
    SimpleGrid,
    Link as ChakraLink,
    Badge,
    Flex,
    Button,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ChevronLeftIcon} from '@app/components/icons'
import {
    PAYMENT_METHODS,
    ORDER_STATUSES,
    SHIPPING_STATUSES,
    DEFAULT_SHIPPING_STATUS
} from '../../constants'
import {InvoiceSummary} from '../../components/order-summary'
import AddressDisplay from '../../components/address-display'
import OrderProducts from '../../components/order-products'
import OrderGiftCards from '../../components/order-giftcards'

import {getOrderConversionRate} from '@app/utils/price-utils'
import {useCustomerType} from '@salesforce/commerce-sdk-react'

const ShipmentInfos = ({shipment, index}) => {
    const {formatMessage} = useIntl()
    const shippingStatusKey = (
        shipment.c_shipmentShippingStatus || shipment.shippingStatus
    ).toUpperCase()
    const shippingStatus = SHIPPING_STATUSES[shippingStatusKey]

    return (
        <Flex alignItems={'center'} key={shipment.shipmentNo}>
            <Text variant={'bodyBase3'}>
                <FormattedMessage
                    defaultMessage="Shipment: {shipment}"
                    id="account_order_history.label.shipment_number"
                    values={{shipment: index + 1}}
                />
            </Text>
            <Badge variant="fresh" ml={8}>
                {formatMessage(shippingStatus || SHIPPING_STATUSES[DEFAULT_SHIPPING_STATUS])}
            </Badge>
        </Flex>
    )
}

ShipmentInfos.propTypes = {
    shipment: PropTypes.object,
    index: PropTypes.number
}

/**
 * Order component displays the details of a specific order within a user's account page.
 * It provides information such as the order date, order number, and shipment details.
 * The component also includes navigation options to return to the order history and
 * access the invoice for the order.
 *
 * Props:
 * @param {Object} order - The order object containing details about the order.
 * @param {boolean} isLoading - A flag indicating whether the order details are currently loading.
 *
 * Features:
 * - Displays a back button to navigate to the order history.
 * - Shows the order details including the order date and order number.
 * - Lists shipment details with their respective statuses.
 * - Provides a link to view the invoice in a new tab.
 *
 * Dependencies:
 * - Uses `useIntl` for internationalization support to format messages and dates.
 * - Utilizes `useHistory` for navigation control.
 * - Employs components like `Stack`, `Box`, `Button`, `Flex`, `Heading`, `Text`, `Badge`, and `Skeleton`
 *   for layout and styling.
 * - Integrates `FormattedMessage` for rendering localized strings.
 */
const Order = ({order, isLoading, isTrackMyOrder = false, orderNo}) => {
    const {formatDate, formatMessage} = useIntl()
    const invoiceLink = `/invoice/${order?.orderNo || orderNo}`
    const history = useHistory()
    const shipment = order?.shipments[0]
    const {shippingAddress, shippingMethod} = shipment || {}
    const isCancelledOrFailedOrder = ['cancelled', 'failed'].includes(order?.status)
    const {isRegistered} = useCustomerType()

    return (
        <>
            <Stack pb={24} borderBottom={'1px solid'} borderColor={'borderGray'}>
                {!isTrackMyOrder && (
                    <Box pb={16}>
                        <Button
                            as={Link}
                            to={'/account/orders'}
                            variant="linkSecondary"
                            leftIcon={<ChevronLeftIcon boxSize={5} mx={-1.5} />}
                            onClick={(e) => {
                                if (history.action === 'PUSH') {
                                    e.preventDefault()
                                    history.goBack()
                                }
                            }}
                        >
                            <FormattedMessage
                                defaultMessage="Back to Order History"
                                id="account_order_detail.link.back_to_history"
                            />
                        </Button>
                    </Box>
                )}
                <Flex flexDirection={['column', 'column', 'row']} gap={24}>
                    <Stack flex={1} spacing={16}>
                        <Box>
                            <Heading
                                as="h1"
                                display="inline-block"
                                size="3xl"
                                textTransform="uppercase"
                                tabIndex="0"
                            >
                                <FormattedMessage
                                    defaultMessage="Order Details"
                                    id="account_order_detail.title.order_details"
                                />
                            </Heading>
                        </Box>
                        {!isLoading ? (
                            <Stack spacing={8}>
                                <Text variant="bodyBase2">
                                    <FormattedMessage
                                        defaultMessage="Ordered: {date}"
                                        id="account_order_detail.label.ordered_date"
                                        values={{
                                            date: formatDate(new Date(order.creationDate), {
                                                year: 'numeric',
                                                day: 'numeric',
                                                month: 'long'
                                            })
                                        }}
                                    />
                                </Text>
                                <Stack direction="row" alignItems="center">
                                    <Text variant="bodyBase2">
                                        <FormattedMessage
                                            defaultMessage="Order Number: {orderNumber}"
                                            id="account_order_detail.label.order_number"
                                            values={{orderNumber: order.orderNo || orderNo}}
                                        />
                                    </Text>
                                </Stack>
                                {['completed', 'cancelled', 'failed'].includes(order.status) && (
                                    <Text>
                                        <Badge
                                            variant={isCancelledOrFailedOrder ? 'fail' : 'fresh'}
                                        >
                                            {formatMessage(ORDER_STATUSES[order.status])}
                                        </Badge>
                                    </Text>
                                )}
                                {!isCancelledOrFailedOrder && (
                                    <Stack
                                        direction={'row'}
                                        justifyContent={'flex-start'}
                                        spacing={24}
                                    >
                                        {order.shipments.length > 1 &&
                                            order.shipments.map((shipment, index) => (
                                                <ShipmentInfos
                                                    key={shipment.shipmentNo}
                                                    shipment={shipment}
                                                    index={index}
                                                />
                                            ))}
                                        {order.shipments.length === 1 && (
                                            <ShipmentInfos
                                                shipment={order.shipments[0]}
                                                index={0}
                                            />
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        ) : (
                            <Skeleton h="20px" w="192px" />
                        )}
                    </Stack>
                    {isRegistered && invoiceLink && (
                        <Button
                            variant={'secondary'}
                            borderColor={'borderGray'}
                            to={invoiceLink}
                            as={Link}
                            onClick={(e) => {
                                e.preventDefault()
                                window.open(e.target.href, '_blank')
                                window.focus()
                            }}
                        >
                            <FormattedMessage
                                defaultMessage="Invoice"
                                id="account_order_detail.action.invoice"
                            />
                        </Button>
                    )}
                </Flex>
            </Stack>
            <Box layerStyle="accountCard">
                <Grid templateColumns={{base: '1fr', md: '60% 1fr'}} gap={24}>
                    <SimpleGrid
                        columns={{base: 1, md: 2}}
                        columnGap={16}
                        rowGap={27}
                        gridAutoRows={'min-content'}
                    >
                        {isLoading ? (
                            <>
                                <Stack>
                                    <Skeleton h="20px" w="84px" />
                                    <Skeleton h="20px" w="112px" />
                                    <Skeleton h="20px" w="56px" />
                                </Stack>
                                <Stack>
                                    <Skeleton h="20px" w="84px" />
                                    <Skeleton h="20px" w="56px" />
                                </Stack>
                                <Stack>
                                    <Skeleton h="20px" w="112px" />
                                    <Skeleton h="20px" w="84px" />
                                    <Skeleton h="20px" w="56px" />
                                </Stack>
                                <Stack>
                                    <Skeleton h="20px" w="60px" />
                                    <Skeleton h="20px" w="84px" />
                                    <Skeleton h="20px" w="56px" />
                                </Stack>
                            </>
                        ) : (
                            <>
                                <Stack spacing={8}>
                                    <Heading as="h2" fontFamily={'body'}>
                                        <Text variant={'bodyXSmall'} textTransform={'uppercase'}>
                                            <FormattedMessage
                                                defaultMessage="Shipping Method"
                                                id="account_order_detail.heading.shipping_method"
                                            />
                                        </Text>
                                    </Heading>
                                    <Box>
                                        <Text variant={'bodyBase2'} pb={4}>
                                            {shippingMethod?.name}
                                        </Text>
                                        {order.shipments.map((shipment, index) => {
                                            /* TODO get tracking link */
                                            const trackingLink = `/account/orders#tracking_${shipment.trackingNumber}`

                                            return shipment.trackingNumber ? (
                                                <Text
                                                    variant={'bodyBase2'}
                                                    pb={4}
                                                    key={shipment.shipmentNo}
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Shipment {shipment} Tracking Number:"
                                                        id="account_order_history.label.shipment_tracking_number"
                                                        values={{
                                                            shipment: index + 1
                                                        }}
                                                    />{' '}
                                                    <ChakraLink
                                                        variant="link"
                                                        href={trackingLink}
                                                        ml={4}
                                                        color={'burgundy'}
                                                        fontWeight={400}
                                                    >
                                                        {shipment.trackingNumber}
                                                    </ChakraLink>
                                                </Text>
                                            ) : null
                                        })}
                                    </Box>
                                </Stack>
                                <Stack spacing={8}>
                                    <Heading as="h2" fontFamily={'body'}>
                                        <Text variant={'bodyXSmall'} textTransform={'uppercase'}>
                                            <FormattedMessage
                                                defaultMessage="Shipping Address"
                                                id="account_order_detail.heading.shipping_address"
                                            />
                                        </Text>
                                    </Heading>
                                    <AddressDisplay address={shippingAddress} mb={'0'} />
                                </Stack>
                                <Stack spacing={8}>
                                    <Heading as="h2" fontFamily={'body'}>
                                        <Text variant={'bodyXSmall'} textTransform={'uppercase'}>
                                            <FormattedMessage
                                                defaultMessage="Payment Method"
                                                id="account_order_detail.heading.payment_method"
                                            />
                                        </Text>
                                    </Heading>
                                    <Stack>
                                        {order?.paymentInstruments.map((paymentInstrument) => {
                                            return (
                                                <Text
                                                    variant={'bodyBase2'}
                                                    key={paymentInstrument.id}
                                                >
                                                    {paymentInstrument.paymentMethodId ===
                                                    PAYMENT_METHODS.GIFT_CERTIFICATE ? (
                                                        <FormattedMessage
                                                            defaultMessage="Gift Card"
                                                            id="invoice.heading.gift_card"
                                                        />
                                                    ) : (
                                                        <>
                                                            {paymentInstrument?.paymentCard
                                                                ?.cardType === 'scheme' ? (
                                                                <>
                                                                    <FormattedMessage
                                                                        defaultMessage="Credit Card"
                                                                        id="checkout_payment.heading.credit_card"
                                                                    />
                                                                    {order.c_Adyen_paymentMethod && (
                                                                        <>
                                                                            {', '}
                                                                            <Text
                                                                                textTransform={
                                                                                    'capitalize'
                                                                                }
                                                                                as={'span'}
                                                                            >
                                                                                {
                                                                                    order.c_Adyen_paymentMethod
                                                                                }
                                                                            </Text>
                                                                        </>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <Text textTransform={'capitalize'}>
                                                                    {
                                                                        paymentInstrument
                                                                            ?.paymentCard?.cardType
                                                                    }
                                                                </Text>
                                                            )}
                                                        </>
                                                    )}
                                                </Text>
                                            )
                                        })}
                                    </Stack>
                                </Stack>
                                <Stack spacing={8}>
                                    <Heading as="h2" fontFamily={'body'}>
                                        <Text variant={'bodyXSmall'} textTransform={'uppercase'}>
                                            <FormattedMessage
                                                defaultMessage="Billing Address"
                                                id="account_order_detail.heading.billing_address"
                                            />
                                        </Text>
                                    </Heading>
                                    <AddressDisplay address={order.billingAddress} mb={'0'} />
                                </Stack>
                            </>
                        )}
                    </SimpleGrid>

                    {!isLoading ? (
                        <Box p={16} background="lightBeige">
                            <InvoiceSummary order={order} />
                        </Box>
                    ) : (
                        <Skeleton h="full" />
                    )}
                </Grid>
            </Box>

            {order?.shipments?.map((shipment, index) => {
                const productsInShipment =
                    order.productItems?.filter(
                        (product) => product.shipmentId === shipment.shipmentId
                    ) || []

                const giftCertificatesInShipment =
                    order.giftCertificateItems?.filter(
                        (giftCertificate) => giftCertificate.shipmentId === shipment.shipmentId
                    ) || []

                const itemsInShipment =
                    productsInShipment.length + giftCertificatesInShipment.length
                const isGiftCertificateShipment = giftCertificatesInShipment.length > 0

                const conversionsRate = getOrderConversionRate(order)
                const shipToCountryCode = order?.c_internationalShippingCountryCode

                return (
                    <Stack spacing={16} key={shipment.shipmentId}>
                        {!isLoading && (
                            <Text variant={'bodyBase2'} textTransform={'uppercase'}>
                                {order.shipments?.length > 1 && (
                                    <>
                                        <FormattedMessage
                                            defaultMessage="Shipment: {shipment}"
                                            id="account_order_history.label.shipment_number"
                                            values={{shipment: index + 1}}
                                        />
                                        {' - '}
                                    </>
                                )}
                                <FormattedMessage
                                    defaultMessage="{count} items"
                                    values={{count: itemsInShipment}}
                                    id="account_order_detail.heading.num_of_items"
                                />
                            </Text>
                        )}

                        <Stack spacing={16}>
                            {isLoading ? (
                                [1, 2, 3].map((i) => (
                                    <Box
                                        key={i}
                                        p={[4, 6]}
                                        border="1px solid"
                                        borderColor={'borderGray'}
                                        borderRadius="base"
                                    >
                                        <Flex width="full" align="flex-start">
                                            <Skeleton boxSize={['88px', 36]} mr={4} />

                                            <Stack spacing={2}>
                                                <Skeleton h="20px" w="112px" />
                                                <Skeleton h="20px" w="84px" />
                                                <Skeleton h="20px" w="140px" />
                                            </Stack>
                                        </Flex>
                                    </Box>
                                ))
                            ) : (
                                <>
                                    {isGiftCertificateShipment ? (
                                        <OrderGiftCards
                                            giftCertificateItems={giftCertificatesInShipment}
                                            conversionsRate={conversionsRate}
                                            shipToCountryCode={shipToCountryCode}
                                        />
                                    ) : (
                                        <OrderProducts
                                            productItems={productsInShipment}
                                            currency={order.currency}
                                            conversionsRate={conversionsRate}
                                            shipToCountryCode={shipToCountryCode}
                                        />
                                    )}
                                </>
                            )}
                        </Stack>
                    </Stack>
                )
            })}
        </>
    )
}

Order.propTypes = {
    order: PropTypes.object,
    isLoading: PropTypes.bool,
    isTrackMyOrder: PropTypes.bool,
    orderNo: PropTypes.string
}

export default Order
